import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';
import { TariffOptionSap, TariffSelectionCardData, TextNodeType } from '@mwe/models';
import { isAllesSicherOptionAlreadyApplied, isArrayWithMinOneItem } from '@mwe/utils';
import { HeadlineService } from '../../headline/headline.service';
import { TariffSelectionStore } from '../services/tariff-selection.store';
import { PriceSheetService, ProfileService } from '@mwe/services';
import { HeadlineComponent } from '../../headline/headline.component';
import { TariffSelectionCardStoreComponent } from '../tariff-selection-card/tariff-selection-card-store.component';
import { TranslateDirective, TranslatePipe } from '@ngx-translate/core';
import { TariffsSelectionButtonComponent } from '../tariffs-selection-button/tariffs-selection-button.component';
import { ExpanderComponent } from '../../expander/expander.component';
import { TariffCardAdditionalOptionsComponent } from '../tariff-card-additional-options/tariff-card-additional-options.component';
import { InfoboxComponent } from '../../infobox/infobox.component';
import { ProductIconComponent } from '../../product-icon/product-icon.component';
import { AsyncPipe, NgTemplateOutlet } from '@angular/common';
import { FeatureToggleEnum, ServiceStateEnum } from '@mwe/constants';
import { CorrelatedCompanyReferencePipe } from '@mwe/ui/pipes';

@Component({
  selector: 'mwe-tariff-selection-container',
  templateUrl: './tariff-selection-container.component.html',
  providers: [HeadlineService],
  imports: [
    HeadlineComponent,
    TariffSelectionCardStoreComponent,
    TranslateDirective,
    TariffsSelectionButtonComponent,
    ExpanderComponent,
    TariffCardAdditionalOptionsComponent,
    TranslatePipe,
    InfoboxComponent,
    ProductIconComponent,
    NgTemplateOutlet,
    CorrelatedCompanyReferencePipe,
    AsyncPipe,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TariffSelectionContainerComponent implements OnInit {
  isReadOnly = input<boolean>(false);
  hideAdditionalOptions = input<boolean>(false);
  headlineReference = input<TextNodeType>('h3');
  // selectedTariffs comes from signal() do not manipulate it!!!! use TariffSelectionStore!!!!!
  selectedTariffs = input.required<TariffSelectionCardData[]>();
  cardCategoryTitleKeyPrefix = input<string>('tariff.selectionCard.stromGas');
  allesSicherTextParams = input<{ url: string }>(undefined);
  isKundeWerden = input<boolean>(false);
  isRelocation = input<boolean>(false);
  readonly hasNewVoucher = input<boolean>(false);
  readonly showTariffTypeFilter = input<boolean>(false);
  readonly tariffSheetUrls = input<Record<string, string>>();
  readonly reloadHandler = input<(tariff: TariffSelectionCardData) => void>();
  protected readonly ServiceStateEnum = ServiceStateEnum;

  headlineService = inject(HeadlineService);
  private profileService = inject(ProfileService);
  private store = inject(TariffSelectionStore);
  priceSheetService = inject(PriceSheetService);

  ngOnInit() {
    this.headlineService.setReferenceHeadline(this.headlineReference());
  }

  getStaticTariffSheetUrl(item: TariffSelectionCardData): string | undefined {
    const key = item?.priceIndication?.tarif?.ISUTarifKey;
    return key ? this.tariffSheetUrls()?.[key] : undefined;
  }

  checkIfAdditionalCardIsVisible(item?: TariffSelectionCardData): boolean {
    const allesSicherForTariffChangeDisabled =
      !this.isKundeWerden() &&
      !this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.TARIFF_CHANGE_ALLES_SICHER_ENABLED);

    const additionalOptionsDisabledForKW =
      this.isKundeWerden() &&
      !this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.NEW_CLIENT_ADDITIONAL_OPTIONS_ENABLED);

    const additionalOptionsForRelocationDisabled =
      !this.isKundeWerden() &&
      this.isRelocation() &&
      !this.profileService.isFeatureToggleEnabled(FeatureToggleEnum.RELOCATION_ADDITIONAL_OPTIONS_ENABLED);

    if (
      this.hideAdditionalOptions() ||
      allesSicherForTariffChangeDisabled ||
      additionalOptionsDisabledForKW ||
      additionalOptionsForRelocationDisabled
    ) {
      return false;
    }

    if (this.isReadOnly()) {
      // user can only store one option of group. not default -> it is selected
      const options = this.getAdditionalOptions(item.userSelection);
      return (
        (options?.length && !options?.[0]?.default) ||
        isAllesSicherOptionAlreadyApplied(item.userSelection, this.store.additionalOptionGroupName())
      );
    }

    return this.hasAdditionalOptions(item.tariffOptionsSap);
  }

  showAllesSicherAlreadyAppliedInfoText(tariffOptions: TariffOptionSap[]): boolean {
    return isAllesSicherOptionAlreadyApplied(tariffOptions, this.store.additionalOptionGroupName());
  }

  private hasAdditionalOptions(options: TariffOptionSap[]): boolean {
    return isArrayWithMinOneItem(this.getAdditionalOptions(options));
  }

  private getAdditionalOptions(options: TariffOptionSap[]): TariffOptionSap[] {
    return options?.filter(t => t.art === this.store.additionalOptionGroupName());
  }
}
